// Libraries
import {useState} from 'react';

// Supermove
import {useMountEffect} from '@supermove/hooks';

// Analytics
import {UseVitallyType, VitallyType} from './VitallyTypes';

// This object is global so it does not cause re-renders.
const Vitally: VitallyType = {
  state: {
    isInitialized: false,
    hasAccount: false,
    hasUser: false,

    // This only works because we load the library once on the client side.
    userId: undefined,
  },

  init: ({token, domain}) => {
    // If the token isn't supplied, do not initialize.
    if (!token) {
      return;
    }

    // @ts-expect-error this is defined by the Vitally plugin
    window.Vitally.init(token, `https://${domain}.api.vitally.io/analytics/v1`);
    Vitally.state.isInitialized = true;
  },

  setAccount: ({accountId}) => {
    if (!Vitally.state.isInitialized) {
      return;
    }
    if (!accountId) {
      return;
    }

    // @ts-expect-error this is defined by the Vitally plugin
    window.Vitally.account({
      accountId, // We use the salesforce ID as the accountId on Vitally.
      traits: {}, // Required to be sent in the request.
    });
    Vitally.state.hasAccount = true;
  },

  setUser: ({userId, accountId, createdAt, fullName, email, role, organizationSlug}) => {
    if (!Vitally.state.isInitialized) {
      return;
    }
    if (!Vitally.state.hasAccount) {
      return;
    }
    if (!accountId || !userId) {
      return;
    }

    // @ts-expect-error this is defined by the Vitally plugin
    window.Vitally.user({
      accountId, // We use the salesforce ID as the accountId on Vitally.
      userId, // We use email as the userId on Vitally.
      traits: {
        // Special Vitally traits
        name: fullName,
        email,
        createdAt,

        // Additional traits
        role,
        organizationIdentifier: organizationSlug,
      },
    });
    Vitally.state.hasUser = true;
    Vitally.state.userId = userId;
  },

  nps: ({productName}) => {
    if (!Vitally.state.isInitialized) {
      return;
    }
    if (!Vitally.state.hasUser) {
      return;
    }

    // @ts-expect-error this is defined by the Vitally plugin
    window.Vitally.nps('survey', {
      productName,
      delay: 1 * 1000, // 1 second
      npsQuestion: ({productName}: {productName: string}) => {
        return `👋 Quick question – how likely are you to recommend ${productName} to others?`;
      },
    });
  },

  // Analytics functions
  identify: ({createdAt, fullName, email, role, organizationSlug, companySalesforceId}) => {
    if (!companySalesforceId || !email) {
      return;
    }

    // For Vitally, we do not want to log internal supermove users. Preventing the setUser from
    // being called will prevent that activity from being logged.
    if (email.includes('@supermove.co') || email.includes('@supermove.com')) {
      return;
    }

    Vitally.setAccount({
      accountId: companySalesforceId,
    });
    Vitally.setUser({
      accountId: companySalesforceId,
      userId: email,
      createdAt,
      fullName,
      email,
      role,
      organizationSlug,
    });
  },
  track: ({event, data}) => {
    const {userId} = Vitally.state;

    if (!userId) {
      return;
    }

    // @ts-expect-error this is defined by the Vitally plugin
    window.Vitally.track({event, userId, properties: data});
  },
  reset: () => {
    // This is a no-op, we don't reset the user at all right now.
  },
};

const useVitally: UseVitallyType = ({token, domain}) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useMountEffect(() => {
    // If the Vitally.js library has already been loaded, do not load again.
    // @ts-expect-error this setting comes from the Vitally plugin.
    if (window.Vitally) {
      return;
    }

    const script = document.createElement('script');
    script.id = 'vitally';
    script.src = `https://cdn.vitally.io/vitally.js/v1/vitally.js`;
    script.async = true;
    script.onload = () => {
      Vitally.init({token, domain});
      setIsLoaded(true);
    };

    // This allows the Vitally global to be defined earlier than the script so that any methods
    // called will be evaluated properly.
    // eslint-disable-next-line
    eval(
      '!function(n,t,i){for(var o=n[t]=n[t]||[],r=function(i){o[i]=o[i]||function(){for(var n=[],t=0;t<arguments.length;t++)n[t]=arguments[t];return o.push([i,n])}},a=0,u=["init","user","account","organization","track","nps"];a<u.length;a++){r(u[a])}}(window,"Vitally");',
    );

    // Add and remove the script from the DOM.
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  });

  return Vitally;
};

export default useVitally;
